/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { SizeMe } from 'react-sizeme';
import { PdfViewer } from '../../PdfViewer/PdfViewer';
import { Scrollable } from '../../../Scrollable/Scrollable';
import { NoPdfPlaceHolder } from '../NoPdfPlaceHolder';
import { MobileDocumentsPicker } from '../MobileDocumentsPicker/MobileDocumentsPicker';
import { zIndex } from '../../../../style/zIndex';
import { MobileDocSubheader } from './MobileDocSubheader';
import { useDocumentViewer } from './hooks/useDocumentViewer';
import { DocumentStatus } from '@shared/constants/document';

export namespace DocumentViewer {
  export interface Props {
    selectedPdfUrl: string | undefined;
    selectedDocStatus: DocumentStatus | undefined;
    shouldShowSigningIframe: boolean;
  }
}

const styles = StyleSheet.create({
  docWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    position: 'relative',
  },
  mobileDocSubheader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'translateY(0%)',
    ['transition']: 'transform 200ms ease-in-out',
    zIndex: zIndex.mobileDocSubheader,
  },
  mobileDocSubheaderIsHidden: {
    transform: 'translateY(-105%)',
  },
});

export const DocumentViewer: React.FC<DocumentViewer.Props> = ({
  selectedPdfUrl,
  selectedDocStatus,
  shouldShowSigningIframe,
}) => {
  const { isMobileView, isMobileSubheaderHidden, shouldShowMobileDocSubheader, onScroll } =
    useDocumentViewer(shouldShowSigningIframe);
  return (
    <SizeMe refreshMode={'debounce'}>
      {({ size }) => (
        <div className={css(styles.docWrapper)} data-it="DocumentViewer">
          {shouldShowMobileDocSubheader && (
            <div
              className={css(styles.mobileDocSubheader, isMobileSubheaderHidden && styles.mobileDocSubheaderIsHidden)}
            >
              <MobileDocSubheader />
            </div>
          )}
          <Scrollable onScroll={onScroll}>
            {selectedPdfUrl ? (
              <PdfViewer width={Number(size.width)} fileUrl={selectedPdfUrl} />
            ) : (
              <NoPdfPlaceHolder status={selectedDocStatus} />
            )}
          </Scrollable>
          {isMobileView && <MobileDocumentsPicker />}
        </div>
      )}
    </SizeMe>
  );
};
